import {
  useState,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
  type ChangeEvent,
  type KeyboardEvent,
  type PointerEvent,
} from "react"
import { Drawer } from "vaul"
import { VaulBottomSheetShell } from "@/shared/ui/VaulBottomSheetShell"
import { X } from "lucide-react"
import { cn, shortenAddress } from "@/shared/lib/utils"
import { useI18n } from "@/shared/i18n"
import {
  calcSheetHeightPx,
  pointerTargetKeepsAmountKeyboard,
  WalletIcon,
} from "./sheetUtils"

import TonIcon from "@/assets/icons/ton-icon.svg?react"
import LinkOffIcon from "@/assets/icons/link-off.svg?react"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BaseAmountSheetProps {
  open: boolean
  onClose: () => void
  /** While true, overlay passes pointer events through (e.g. wallet modal on top). */
  isProcessing: boolean

  title: string
  /** Formatted in-app balance, e.g. "12.34" */
  balance: string

  /** When empty, wallet chip / disconnect hidden; amount field non-editable unless overridden. */
  address?: string | null
  walletBalance: number | null
  onDisconnect?: () => void
  /** Default: editable only when `address` is non-empty. */
  amountEditable?: boolean

  // ── Amount zone ──
  amountInputRef: RefObject<HTMLInputElement>
  inputDisplayValue: string
  hasValidAmount: boolean
  amountLabel: string
  /** Orange warning shown above the number, e.g. "Minimum withdrawal: 5 TON" */
  amountWarning?: string | null
  /** Whether the number itself should render in orange (invalid / below min) */
  amountIsWarning?: boolean
  maxAmount?: number
  onAmountChange: (e: ChangeEvent<HTMLInputElement>) => void
  onAmountKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void

  // ── Slots rendered ABOVE the amount zone ──
  topContent?: ReactNode

  /** When set, replaces the amount zone entirely (e.g. the gifts-deposit info screen). */
  body?: ReactNode

  // ── Slots rendered BELOW the wallet chip ──
  extraInfo?: ReactNode
  presets?: ReactNode
  cta: ReactNode
}

// ─── BaseAmountSheet ──────────────────────────────────────────────────────────

export function BaseAmountSheet({
  open,
  onClose,
  isProcessing,
  title,
  balance,
  address,
  walletBalance,
  onDisconnect,
  amountEditable: amountEditableProp,
  amountInputRef,
  inputDisplayValue,
  hasValidAmount,
  amountLabel,
  amountWarning,
  amountIsWarning = false,
  onAmountChange,
  onAmountKeyDown,
  topContent,
  body,
  extraInfo,
  presets,
  cta,
}: BaseAmountSheetProps) {
  const { t } = useI18n()

  const sheetHeightPx = useMemo(() => calcSheetHeightPx(open), [open])

  const innerRef = useRef<HTMLDivElement>(null)

  const [isAmountFocused, setIsAmountFocused] = useState(false)

  const walletConnected = Boolean(address && address.length > 0)
  const amountEditable = amountEditableProp ?? walletConnected

  const handleOpenChange = (v: boolean) => {
    if (!v) onClose()
  }

  const focusAmountInput = () => {
    const input = amountInputRef.current
    if (!input || isProcessing || !amountEditable) return
    try {
      input.focus({ preventScroll: true })
    } catch {
      input.focus()
    }
  }

  const blurOnOutsideTap = (e: PointerEvent) => {
    if (pointerTargetKeepsAmountKeyboard(e.target)) return
    amountInputRef.current?.blur()
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={handleOpenChange}
      repositionInputs={false}
      /* With patched Radix `modal={false}` + TonConnect on top, vaul must be non-modal too:
         otherwise `onPointerDownOutside` treats outside taps as dismiss. `Drawer.Overlay` is null
         when `modal={false}` — keep the same dimmer as a plain div + tap-to-dismiss on backdrop. */
      modal={false}
    >
      <Drawer.Portal>
        <div
          aria-hidden
          data-vaul-overlay=""
          className={cn(
            "fixed inset-0 z-[200] bg-black/60",
            isProcessing && "pointer-events-none"
          )}
          onPointerDown={(e) => {
            amountInputRef.current?.blur()
            if (e.target === e.currentTarget && !isProcessing) onClose()
          }}
        />

        <VaulBottomSheetShell
          ref={innerRef}
          onPointerDownCapture={blurOnOutsideTap}
          panelClassName={cn(
            "rounded-t-[24px] bg-[#24242E]",
            "flex flex-col",
            "overflow-hidden",
            sheetHeightPx == null && "h-[95dvh]"
          )}
          panelStyle={
            sheetHeightPx != null
              ? { height: sheetHeightPx, maxHeight: sheetHeightPx }
              : undefined
          }
        >
          <Drawer.Handle className="mx-auto mb-0 mt-1.5 h-1 w-8 shrink-0 rounded-full bg-white/10" />

          <div className="flex shrink-0 items-center justify-between px-5 pb-4 pt-3">
            <div className="w-8" />
            <div className="flex flex-col items-center gap-0.5">
              <Drawer.Title className="text-2xl font-semibold tracking-[-0.5px] text-white">
                {title}
              </Drawer.Title>
              <p className="text-xs">
                <span className="text-white/60">
                  {t("common.trixBalance")}{" "}
                </span>
                <span className="text-green-100">
                  {balance} {t("common.ton")}
                </span>
              </p>
            </div>
            <Drawer.Close asChild>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/50 transition-colors active:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </Drawer.Close>
          </div>

          {topContent}

          <div
            className={cn(
              "relative flex min-h-0 flex-1 flex-col items-center justify-center py-2",
              !body && "px-6"
            )}
          >
            {body ?? (
            <div
              data-amount-field
              className={cn(
                "relative inline-flex max-w-full flex-col items-center gap-1 rounded-2xl px-2 py-1",
                amountEditable && !isProcessing
                  ? "cursor-pointer"
                  : "pointer-events-none cursor-default",
                !amountEditable && !amountWarning && "opacity-30"
              )}
              onClick={amountEditable ? focusAmountInput : undefined}
            >
              <input
                ref={amountInputRef}
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                value={inputDisplayValue}
                onFocus={() => setIsAmountFocused(true)}
                onBlur={() => setIsAmountFocused(false)}
                onChange={onAmountChange}
                onKeyDown={onAmountKeyDown}
                disabled={isProcessing || !amountEditable}
                aria-label={`${title} ${t("common.amount").toLowerCase()}`}
                className="pointer-events-none absolute left-1/2 top-1/2 h-px w-px -translate-x-1/2 -translate-y-1/2 opacity-0"
              />

              {amountWarning ? (
                <p className="mb-1 select-none text-[13px] font-medium text-[#F97316]">
                  {amountWarning}
                </p>
              ) : (
                <p className="select-none text-center text-[14px] font-normal leading-[17px] tracking-[-0.5px] text-white">
                  {amountLabel}
                </p>
              )}

              <div className="flex flex-row items-center justify-center gap-1">
                <span className="inline-flex items-center">
                  <span
                    className={cn(
                      "text-5xl/[130%] font-semibold tabular-nums tracking-[-0.25px] transition-colors",
                      amountIsWarning
                        ? "text-[#F97316]"
                        : hasValidAmount
                          ? "text-white"
                          : "text-white/60"
                    )}
                  >
                    {inputDisplayValue || "0"}
                  </span>
                  {amountEditable && isAmountFocused && (
                    <span
                      aria-hidden
                      className="ml-1 h-12 w-0.5 shrink-0 animate-pulse self-center rounded-full bg-[#B1FE64]"
                    />
                  )}
                </span>
                <TonIcon className="size-10 shrink-0 text-white/60" />
              </div>
            </div>
            )}
          </div>

          <div className="shrink-0">
            <div
              className={cn(
                "mb-3 mt-1 flex justify-center",
                !(walletConnected && onDisconnect) &&
                  "pointer-events-none invisible"
              )}
            >
              <div className="flex h-9 items-center gap-2 rounded-full bg-white/10 pl-2.5 pr-2">
                <WalletIcon />
                <span className="text-sm/none font-medium text-white">
                  {address ? shortenAddress(address, 4) : "—"}
                </span>
                {walletBalance != null && (
                  <span className="text-sm/none text-white/60">
                    {walletBalance.toFixed(2)} {t("common.ton")}
                  </span>
                )}
                <button
                  type="button"
                  onClick={onDisconnect}
                  className="ml-2 flex size-5 items-center justify-center rounded-full bg-white/10 transition-colors active:bg-white/20"
                  aria-label={t("common.disconnectWallet")}
                >
                  <LinkOffIcon className="size-3 text-white/60" />
                </button>
              </div>
            </div>

            {extraInfo && <div className="mb-3">{extraInfo}</div>}

            {presets != null && <div className="mb-3 px-4">{presets}</div>}

            <div className="px-4 pb-8">{cta}</div>
          </div>
        </VaulBottomSheetShell>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
